import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import { getStoredUser } from '../../utils/authStorage';
import { useExamResourceList } from '../../hooks/useExamResourceList';
import './ExamResourceList.css';

const resourceLabels = {
  'question-bank': 'Question Bank',
  lecture: 'Lectures',
  'short-notes': 'Short Notes',
  'rapid-fire': 'Rapid Fire',
  'flash-cards': 'Flash Cards',
  'audio-podcast': 'Audio Podcasts',
  infographics: 'Infographics',
  quiz: 'Quizzes',
  pyp: 'Previous Year Papers',
  'test-series': 'Test Series',
};

function getItemTitle(item) {
  return item?.subject_name || item?.quiz_name || item?.series_name || item?.name || item?.title || 'Untitled resource';
}

function getItemDescription(item) {
  return item?.subject_description || item?.description || item?.quiz_description || '';
}

export default function ExamResourceList() {
  const { resourceType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const context = location.state || {};
  const storedUser = getStoredUser();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const stateId = Number(context.states_id || storedUser?.states_id || storedUser?.state_id || 0);
  const params = {
    page,
    states_id: stateId,
    exams_id: Number(context.exams_id || 0),
    category_id: Number(context.category_id || 0),
    free_paid: context.free_paid || 'F',
    search_query: context.search_query || '',
    subject_category_id: context.subject_category_id || undefined,
    is_mock: context.is_mock || undefined,
  };
  const { data, isLoading, isFetching, isError, error } = useExamResourceList(resourceType, params);

  useEffect(() => {
    if (!data?.items) return;
    setItems((previous) => (page === 1 ? data.items : [...previous, ...data.items]));
  }, [data, page]);

  const title = resourceLabels[resourceType] || 'Exam Resources';
  const canLoadMore = data?.pagination?.currentPage < data?.pagination?.totalPages;

  return (
    <HomeDashboardLayout showHeader={false}>
      <main className="exam-resource-page page-padding">
        <div className="categories-page-header mb-0">
          <button type="button" className="categories-back-button" onClick={() => navigate(-1)} aria-label="Back">
            <i className="bi bi-chevron-left" />
          </button>
          <div />
          <HeaderProfileSection />
        </div>
        <header className="exam-resource-page__header">
          <p>{context.exam_name || 'Exam Course'}</p>
          <h1>{title}</h1>
          <span>{context.free_paid === 'P' ? 'Course Content' : 'Demo Content'}</span>
        </header>
        {isLoading && page === 1 ? <div className="exam-resource-page__status">Loading {title.toLowerCase()}...</div> : null}
        {isError ? <div className="exam-resource-page__status">{error?.response?.data?.message || 'Unable to load resources.'}</div> : null}
        {!isLoading && !isError && !items.length ? <div className="exam-resource-page__status">No {title.toLowerCase()} are available.</div> : null}
        <section className="exam-resource-page__grid">
          {items.map((item, index) => (
            <article className="exam-resource-card" key={item?.subject_id || item?.quiz_id || item?.series_id || index}>
              <span className="exam-resource-card__number">{String(index + 1).padStart(2, '0')}</span>
              <h2>{getItemTitle(item)}</h2>
              {getItemDescription(item) ? <p>{getItemDescription(item)}</p> : null}
              <span className="exam-resource-card__meta">{item?.free_paid === 'P' ? 'Prime' : 'Free'}</span>
            </article>
          ))}
        </section>
        {canLoadMore ? (
          <button type="button" className="exam-resource-page__more" onClick={() => setPage((current) => current + 1)} disabled={isFetching}>
            {isFetching ? 'Loading...' : 'Load more'}
          </button>
        ) : null}
      </main>
    </HomeDashboardLayout>
  );
}
